import React, { useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import { InputText } from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import { DEFAULT_AREA_ID, DEFAULT_PROPERTY_ID, projectSelectStyles, SelectOption } from './helper';

type BuildingSelectOption = {
  value: string;
  label: string;
  projectId?: string;
};

export type CalendarFilterValues = {
  unitType: SelectOption;
  user: SelectOption;
  area: SelectOption;
  project: SelectOption[];
  unitTypeSearch: FormElementData<string>;
  unitTypeGroup: SelectOption;
  buildingSelect: BuildingSelectOption;
};

type CalendarFiltersPanelProps = {
  areaOptions: SelectOption[];
  allProjects: SelectOption[];
  allUsers: SelectOption[];
  allUnitTypes: SelectOption[];
  unitTypeGroupsList: { value: string; label: string }[];
  allBuildingOptions: { id?: string; name: string; projectId?: string }[];
  onSearch: (filters: CalendarFilterValues) => void;
  onReset: () => void;
};

const createEmptySelectOption = (): SelectOption => ({ value: '', label: '' });
const createDefaultAreaOption = (): SelectOption => ({
  value: DEFAULT_AREA_ID,
  label: 'new Cairo'
});
const createDefaultProjectSelection = (): SelectOption[] => [
  { value: DEFAULT_PROPERTY_ID, label: 'Boutique Nest' }
];
const createEmptyUnitTypeSearch = (): FormElementData<string> => ({
  value: '',
  isValid: true
});
const createEmptyBuildingSelect = (): BuildingSelectOption => ({
  value: '',
  label: '',
  projectId: ''
});

export const CalendarFiltersPanel: React.FC<CalendarFiltersPanelProps> = ({
  areaOptions,
  allProjects,
  allUsers,
  allUnitTypes,
  unitTypeGroupsList,
  allBuildingOptions,
  onSearch,
  onReset
}) => {
  const [unitType, setUnitType] = useState<SelectOption>(createEmptySelectOption());
  const [user, setUser] = useState<SelectOption>(createEmptySelectOption());
  const [area, setArea] = useState<SelectOption>(createDefaultAreaOption());
  const [project, setProject] = useState<SelectOption[]>(createDefaultProjectSelection());
  const [unitTypeSearch, setUnitTypeSearch] = useState<FormElementData<string>>(
    createEmptyUnitTypeSearch()
  );
  const [unitTypeGroup, setUnitTypeGroup] = useState<SelectOption>(createEmptySelectOption());
  const [buildingSelect, setBuildingSelect] = useState<BuildingSelectOption>(
    createEmptyBuildingSelect()
  );

  const filteredBuildingOptions = useMemo(() => {
    const selectedValues = project.map((p) => p.value);
    if (selectedValues.length === 0) {
      return allBuildingOptions;
    }
    return allBuildingOptions.filter((b) => selectedValues.includes(b.projectId || ''));
  }, [allBuildingOptions, project]);

  const filteredProjects = useMemo(() => {
    if (!area.value) return allProjects;
    return allProjects.filter((p) => p.areaId?.includes(area.value));
  }, [allProjects, area.value]);

  const filteredUnitTypes = useMemo(() => {
    let result = allUnitTypes;

    if (area.value) {
      result = result.filter((unit) => unit?.areaId?.includes(area.value));
    }

    if (project.length > 0) {
      const selectedProjectIds = project.map((p) => p.value);
      result = result.filter((unit) =>
        selectedProjectIds.some((id) => unit?.propertyId?.includes(id))
      );
    }

    if (user.value) {
      result = result.filter((unit) => unit?.userId?.includes(user.value));
    }

    return result;
  }, [allUnitTypes, area.value, project, user.value]);

  const filteredUsers = useMemo(() => {
    let result = allUsers;

    if (area.value) {
      result = result.filter((u) => u?.areaId?.includes(area.value));
    }

    if (project.length > 0) {
      const selectedProjectIds = project.map((p) => p.value);
      result = result.filter((u) => selectedProjectIds.some((id) => u?.propertyId?.includes(id)));
    }

    return result;
  }, [allUsers, area.value, project]);

  const handleSearchClick = () =>
    onSearch({
      unitType,
      user,
      area,
      project,
      unitTypeSearch,
      unitTypeGroup,
      buildingSelect
    });

  const handleResetClick = () => {
    setProject(createDefaultProjectSelection());
    setUnitType(createEmptySelectOption());
    setUser(createEmptySelectOption());
    setArea(createDefaultAreaOption());
    setUnitTypeSearch(createEmptyUnitTypeSearch());
    setUnitTypeGroup(createEmptySelectOption());
    setBuildingSelect(createEmptyBuildingSelect());
    onReset();
  };

  return (
    <div className="relative mx-[10%] mt-4 rounded-lg bg-white px-4 py-4 md:px-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1">
          <div className="text-base font-medium text-gray-600">Search</div>
          <InputText
            data={{
              value: unitTypeSearch.value,
              placeholder: 'Search by unit, project, group, area, user…'
            }}
            config={{
              maxRows: 1,
              autoFocus: false
            }}
            eventHandlers={{
              onChangeHandler: (value: string, isValid: boolean) => {
                setUnitTypeSearch({ value, isValid });
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-base font-medium text-gray-600">Area</div>
          <Select
            value={{ value: area.value, label: area.label }}
            options={areaOptions}
            className="text-lg"
            onChange={(value: ValueType<{ value: string; label: string }, false>) => {
              setArea(value ? value : createEmptySelectOption());
              setProject([]);
              setUnitType(createEmptySelectOption());
              setUser(createEmptySelectOption());
              setUnitTypeGroup(createEmptySelectOption());
              setBuildingSelect(createEmptyBuildingSelect());
            }}
          />
        </div>

        <div className="flex flex-col gap-1 sm:col-span-2 lg:col-span-2">
          <div className="text-base font-medium text-gray-600">Project</div>
          <Select
            isMulti
            closeMenuOnSelect={false}
            isClearable
            placeholder="Select one or more projects..."
            styles={projectSelectStyles}
            value={project}
            options={filteredProjects.map((p) => ({
              value: p.value,
              label: p.label
            }))}
            className="text-lg"
            onChange={(newValue: ValueType<{ value: string; label: string }, true>) => {
              const selected = (newValue || []) as { value: string; label: string }[];
              setProject(selected);
              setUnitType(createEmptySelectOption());
              setUnitTypeGroup(createEmptySelectOption());
              setBuildingSelect(createEmptyBuildingSelect());
              setUser(createEmptySelectOption());
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-base font-medium text-gray-600">Homeowner</div>
          <Select
            value={{ value: user.value, label: user.label }}
            options={filteredUsers.map((item) => ({
              value: item.value,
              label: item.label
            }))}
            className="text-lg"
            onChange={(value: ValueType<{ value: string; label: string }, false>) => {
              setUser(
                value ? { value: value.value, label: value.label } : createEmptySelectOption()
              );
              setUnitTypeGroup(createEmptySelectOption());
              setUnitType(createEmptySelectOption());
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-base font-medium text-gray-600">Rooms</div>
          <Select
            value={{ value: unitType.value, label: unitType.label }}
            options={filteredUnitTypes.map((room) => ({
              value: room.value,
              label: room.label
            }))}
            className="text-lg"
            onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
              setUnitType(
                value ? { value: value.value || '', label: value.label } : createEmptySelectOption()
              );
              setUnitTypeGroup(createEmptySelectOption());
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-base font-medium text-gray-600">Unit type</div>
          <Select
            value={{ value: unitTypeGroup.value, label: unitTypeGroup.label }}
            className="text-lg"
            options={unitTypeGroupsList}
            onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
              setUnitTypeGroup(
                value ? { value: value.value || '', label: value.label } : createEmptySelectOption()
              );
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-base font-medium text-gray-600">Building</div>
          <Select
            isClearable
            value={{ value: buildingSelect.value, label: buildingSelect.label }}
            className="text-lg"
            options={filteredBuildingOptions.map((b) => ({
              value: b.id ?? '',
              label: b.name ?? ''
            }))}
            onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
              const selectedValue = value?.value ?? '';
              if (selectedValue) {
                const selectedBuilding = filteredBuildingOptions.find(
                  (b) => b.id === selectedValue
                );
                setBuildingSelect({
                  value: selectedValue,
                  label: value?.label ?? '',
                  projectId: selectedBuilding?.projectId || ''
                });
              } else {
                setBuildingSelect(createEmptyBuildingSelect());
              }
            }}
          />
        </div>
      </div>
      <div className="mt-3 flex w-full items-center justify-end gap-2">
        <Button
          onClick={handleSearchClick}
          tracking={{ event: 'search_performed', scope: 'unittypes' }}
          size={ButtonSize.small}
        >
          Search
        </Button>

        <Button
          onClick={handleResetClick}
          tracking={{ event: 'search_cleared', scope: 'unittypes' }}
          size={ButtonSize.small}
          type={ButtonType.gray}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};
